/**
 * Pipe curves and groups for network visualization paths
 */

import type * as THREE_NS from 'three';
import type { CurveType } from '../utils/curves.js';
import { createNaturalCubicSpline, createHermiteSpline, createBSpline } from '../utils/curves.js';

export interface PipeCurves {
  upPipeCurve: any;
  downPipeCurve: any;
  upPipeGroup: THREE_NS.Group;
  downPipeGroup: THREE_NS.Group;
}

export interface PipeConfig {
  mainPipeRadius: number;
  smallPipeRadius: number;
  smallSphereY: number;
  extendedPipeLength: number;
  cameraDistance: number;
}

/**
 * Create pipe curves and groups
 */
export function createPipes(
  scene: THREE_NS.Scene,
  THREE: typeof THREE_NS,
  config: PipeConfig,
  curveType: CurveType = 'bezier'
): PipeCurves {
  const { Group, Vector3, SphereGeometry, MeshBasicMaterial, Mesh } = THREE as any;
  const { mainPipeRadius, smallPipeRadius, smallSphereY, extendedPipeLength, cameraDistance } = config;

  // Debug helpers (hidden by default)
  const sphereGeometry = new SphereGeometry(5, 32, 32);
  const sphereMaterial = new MeshBasicMaterial({
    color: 0x00ff00,
    opacity: 0.5,
    transparent: true,
    wireframe: false,
  });
  const centerSphere = new Mesh(sphereGeometry, sphereMaterial);
  centerSphere.position.set(0, 0, 0);
  centerSphere.visible = false;
  scene.add(centerSphere);

  const smallSphereGeometry = new SphereGeometry(2, 32, 32);
  const smallSphere = new Mesh(smallSphereGeometry, sphereMaterial);
  smallSphere.position.set(0, smallSphereY, 0);
  smallSphere.visible = false;
  scene.add(smallSphere);

  // Create transform groups for each L-shaped pipe
  const upPipeGroup = new Group();
  upPipeGroup.position.set(0, 0, 0);
  scene.add(upPipeGroup);

  const downPipeGroup = new Group();
  downPipeGroup.position.set(0, 0, 0);
  scene.add(downPipeGroup);

  // Create up-pipe curve (L-shaped curve pointing up - main pipe)
  const vertLength = extendedPipeLength*2;
  const horzLength = cameraDistance*2;
  const numPoints = 128;
  const cornerRadius = Math.min(vertLength, horzLength) * 0.3;

  // Up-pipe control points
  const upP0 = new Vector3(0.0, -vertLength, 0.0);
  const upP1 = new Vector3(0.0, -cornerRadius, 0.0);
  const upP2 = new Vector3(cornerRadius, 0.0, 0.0);
  const upP3 = new Vector3(horzLength, 0.0, 0.0);

  // Down-pipe control points
  const downP0 = new Vector3(0.0, vertLength, 0.0);
  const downP1 = new Vector3(0.0, cornerRadius, 0.0);
  const downP2 = new Vector3(cornerRadius, 0.0, 0.0);
  const downP3 = new Vector3(horzLength, 0.0, 0.0);

  const { CubicBezierCurve3, CatmullRomCurve3 } = THREE as any;

  let upPipeCurve: any;
  let downPipeCurve: any;

  if (curveType === 'bezier') {
    if (CubicBezierCurve3) {
      upPipeCurve = new CubicBezierCurve3(upP0, upP1, upP2, upP3);
      downPipeCurve = new CubicBezierCurve3(downP0, downP1, downP2, downP3);
    } else {
      // Fallback: create CatmullRom from sampled points
      const upPoints: any[] = [];
      const downPoints: any[] = [];
      for (let i = 0; i <= numPoints; i++) {
        const t = i / numPoints;
        const mt = 1 - t;
        const mt2 = mt * mt;
        const mt3 = mt2 * mt;
        const t2 = t * t;
        const t3 = t2 * t;

        const upPoint = new Vector3()
          .addScaledVector(upP0, mt3)
          .addScaledVector(upP1, 3 * mt2 * t)
          .addScaledVector(upP2, 3 * mt * t2)
          .addScaledVector(upP3, t3);
        upPoints.push(upPoint);

        const downPoint = new Vector3()
          .addScaledVector(downP0, mt3)
          .addScaledVector(downP1, 3 * mt2 * t)
          .addScaledVector(downP2, 3 * mt * t2)
          .addScaledVector(downP3, t3);
        downPoints.push(downPoint);
      }
      upPipeCurve = new CatmullRomCurve3(upPoints);
      downPipeCurve = new CatmullRomCurve3(downPoints);
    }
  } else if (curveType === 'natural-cubic') {
    const upControlPoints = [upP0, upP1, upP2, upP3];
    const downControlPoints = [downP0, downP1, downP2, downP3];
    upPipeCurve = createNaturalCubicSpline(upControlPoints, THREE);
    downPipeCurve = createNaturalCubicSpline(downControlPoints, THREE);
  } else if (curveType === 'hermite') {
    const upT0 = new Vector3().subVectors(upP1, upP0).normalize().multiplyScalar(10);
    const upT1 = new Vector3().subVectors(upP3, upP2).normalize().multiplyScalar(10);
    const downT0 = new Vector3().subVectors(downP1, downP0).normalize().multiplyScalar(10);
    const downT1 = new Vector3().subVectors(downP3, downP2).normalize().multiplyScalar(10);
    upPipeCurve = createHermiteSpline(upP0, upT0, upP3, upT1, THREE);
    downPipeCurve = createHermiteSpline(downP0, downT0, downP3, downT1, THREE);
  } else if (curveType === 'b-spline') {
    const upControlPoints = [upP0, upP1, upP2, upP3];
    const downControlPoints = [downP0, downP1, downP2, downP3];
    upPipeCurve = createBSpline(upControlPoints, THREE, 3);
    downPipeCurve = createBSpline(downControlPoints, THREE, 3);
  }

  return {
    upPipeCurve,
    downPipeCurve,
    upPipeGroup,
    downPipeGroup,
  };
}

// Track last log time for 4-second interval logging
let lastLogTime = 0;
const LOG_INTERVAL_MS = 4000; // 4 seconds

/**
 * Update pipes to track camera rotation
 * Both pipes rotate around Y-axis (Zenith-Nadir) so their horizontal protrusions face the camera
 * Uses the same calculation as the arrow but without the 90-degree offset
 */
export function updatePipes(
  downPipeGroup: THREE_NS.Group,
  upPipeGroup: THREE_NS.Group,
  camera: THREE_NS.Camera,
  THREE: typeof THREE_NS
): void {
  if (!camera) return;

  const { Vector3, Quaternion } = THREE as any;

  // Ensure camera world matrix is up to date
  camera.updateMatrixWorld(true);

  // Get camera position in world space
  const cameraPosition = new Vector3();
  camera.getWorldPosition(cameraPosition);

  // Calculate direction from pipe origin (0,0,0) to camera
  const direction = new Vector3()
    .subVectors(cameraPosition, new Vector3(0, 0, 0))
    .normalize();

  // Project to XZ plane for horizontal rotation only (Y-axis rotation)
  const horizontalDirection = new Vector3(
    direction.x,
    0,
    direction.z
  );
  
  // Check if camera is directly above or below
  const horizontalLength = horizontalDirection.length();
  if (horizontalLength < 0.001) {
    return; // Keep current rotation
  }
  
  horizontalDirection.normalize();

  // Use quaternion to rotate only around Y-axis (same as arrow, but no 90-degree offset)
  // Rotate +X axis to point at camera horizontally
  const forwardX = new Vector3(1, 0, 0);
  const quaternion = new Quaternion();
  quaternion.setFromUnitVectors(forwardX, horizontalDirection);

  // Local backward translation: move pipes back along their local -X axis
  // This moves them away from the camera within their local transform space
  const localBackwardDistance = 15; // Distance to step back locally
  const localBackward = new Vector3(-localBackwardDistance, 0, 0);

  // Apply rotation and local translation to both pipe groups
  if (downPipeGroup) {
    downPipeGroup.quaternion.copy(quaternion);
    // Apply local translation: rotate the backward vector by the quaternion
    const worldBackward = localBackward.clone().applyQuaternion(quaternion);
    downPipeGroup.position.copy(worldBackward);
  }

  if (upPipeGroup) {
    upPipeGroup.quaternion.copy(quaternion);
    // Apply local translation: rotate the backward vector by the quaternion
    const worldBackward = localBackward.clone().applyQuaternion(quaternion);
    upPipeGroup.position.copy(worldBackward);
  }
}
